import { Component, type ReactNode, type ErrorInfo } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

const CHUNK_ERROR_RELOAD_KEY = 'mediflow_care_chunk_reload';

interface Props {
    children: ReactNode;
    fallback?: ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
    isChunkError: boolean;
}

function isChunkLoadError(error: Error): boolean {
    return (
        error.name === 'ChunkLoadError' ||
        error.message.includes('Loading chunk') ||
        error.message.includes('Failed to fetch dynamically imported module') ||
        error.message.includes('Unable to preload CSS')
    );
}

function ErrorScreen({
    isChunkError,
    message,
    onRetry,
}: {
    isChunkError: boolean;
    message: string;
    onRetry: () => void;
}) {
    return (
        <div className="min-h-screen flex items-center justify-center bg-neutral-50 dark:bg-neutral-900 p-6">
            <div className="max-w-md w-full text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-red-100 dark:bg-red-900/30 mb-6">
                    <AlertTriangle className="w-8 h-8 text-red-600 dark:text-red-400" />
                </div>
                <h1 className="text-xl font-bold text-neutral-900 dark:text-neutral-100 mb-2">
                    {isChunkError ? 'Seite konnte nicht geladen werden' : 'Etwas ist schiefgelaufen'}
                </h1>
                <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-6">
                    {isChunkError
                        ? 'Beim Laden des Bereichs ist ein Update- oder Chunk-Fehler aufgetreten. Die Seite kann neu geladen werden.'
                        : message}
                </p>
                <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
                    <button
                        onClick={onRetry}
                        className="inline-flex min-h-[44px] items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-brand-500 text-white text-sm font-medium hover:bg-brand-600 transition-colors"
                    >
                        <RefreshCw className="w-4 h-4" />
                        {isChunkError ? 'Neu laden' : 'Erneut versuchen'}
                    </button>
                </div>
            </div>
        </div>
    );
}

export class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = { hasError: false, error: null, isChunkError: false };
    }

    static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error, isChunkError: isChunkLoadError(error) };
    }

    componentDidCatch(error: Error, info: ErrorInfo) {
        console.error('[MediFlow Pflege] ErrorBoundary:', error, info.componentStack);
    }

    componentDidMount() {
        sessionStorage.removeItem(CHUNK_ERROR_RELOAD_KEY);
    }

    handleReset = () => {
        this.setState({ hasError: false, error: null, isChunkError: false });
    };

    handleReload = () => {
        const already = sessionStorage.getItem(CHUNK_ERROR_RELOAD_KEY);
        if (!already) {
            sessionStorage.setItem(CHUNK_ERROR_RELOAD_KEY, '1');
            window.location.reload();
        } else {
            sessionStorage.removeItem(CHUNK_ERROR_RELOAD_KEY);
            this.handleReset();
        }
    };

    render() {
        if (!this.state.hasError) return this.props.children;
        if (this.props.fallback) return this.props.fallback;

        return (
            <ErrorScreen
                isChunkError={this.state.isChunkError}
                message={this.state.error?.message ?? 'Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie es erneut.'}
                onRetry={this.state.isChunkError ? this.handleReload : this.handleReset}
            />
        );
    }
}
